import { getRemoteAudioElement } from './remoteAudio'
import { supportsOutputRouting } from './platformCapabilities'

export const SPEAKER_TEST_DURATION_IN_MS = 3000

/**
 * A rising and falling C major arpeggio with a held last note: `[frequency in Hz, length in seconds]`,
 * adding up to the three seconds above. A tune rather than a beep, so an agent can tell it from a
 * ringtone or a system sound and hears long enough to find which ear it is in.
 */
export const SPEAKER_TEST_MELODY = Object.freeze([
  [523.25, 0.375], // C5
  [659.25, 0.375], // E5
  [783.99, 0.375], // G5
  [1046.5, 0.375], // C6
  [783.99, 0.375], // G5
  [659.25, 0.375], // E5
  [523.25, 0.75] // C5, held
])

const NOTE_GAIN = 0.2
const NOTE_ATTACK_SECONDS = 0.02
// The gap left at the end of each note, so repeated pitches would still read as separate notes.
const NOTE_RELEASE_SECONDS = 0.05

function _audioContextClass() {
  if (typeof window === 'undefined') {
    return null
  }

  return window.AudioContext || window.webkitAudioContext || null
}

export function canPlaySpeakerTest() {
  const AudioContextClass = _audioContextClass()

  return Boolean(AudioContextClass) && typeof AudioContextClass.prototype.createMediaStreamDestination === 'function'
}

/**
 * Each note its own oscillator with its own envelope, all into one stream, so the notes start and
 * stop cleanly instead of clicking.
 */
function _scheduleMelody(context, destination) {
  const startAt = context.currentTime

  let offset = 0

  SPEAKER_TEST_MELODY.forEach(([frequency, length]) => {
    const oscillator = context.createOscillator()
    const gain = context.createGain()
    const noteStart = startAt + offset
    const noteEnd = noteStart + length

    oscillator.type = 'triangle'
    oscillator.frequency.value = frequency

    gain.gain.setValueAtTime(0, noteStart)
    gain.gain.linearRampToValueAtTime(NOTE_GAIN, noteStart + NOTE_ATTACK_SECONDS)
    gain.gain.linearRampToValueAtTime(0, noteEnd - NOTE_RELEASE_SECONDS)

    oscillator.connect(gain)
    gain.connect(destination)

    oscillator.start(noteStart)
    oscillator.stop(noteEnd)

    offset += length
  })
}

/**
 * Plays the test tune through `#softphone-remote-audio` - the element the caller is heard through -
 * so the test exercises the real path: the same element, the same sink. A tune through a separate
 * `Audio` would prove the speaker works and say nothing about where the call will go.
 *
 * Generated rather than shipped as a file, and fed in as a `MediaStream` because that is how the
 * call arrives too. The element is pointed at the chosen sink first, so the test also works offline,
 * before any transport has routed it.
 *
 * The audio context is created before anything is awaited: Safari only lets one start inside the
 * click that asked for it.
 *
 * A call that attaches its own stream during the tune wins: the element is only handed back to
 * silence if it is still holding the tune.
 */
export async function playSpeakerTestTone(deviceId = null) {
  const AudioContextClass = _audioContextClass()
  const element = getRemoteAudioElement()

  if (!AudioContextClass || !element) {
    throw new Error('The speaker test is not available in this browser')
  }

  const context = new AudioContextClass()
  const destination = context.createMediaStreamDestination()
  const toneStream = destination.stream

  try {
    if (supportsOutputRouting()) {
      await element.setSinkId(deviceId || '')
    }

    if (context.state === 'suspended' && typeof context.resume === 'function') {
      await context.resume()
    }

    element.srcObject = toneStream

    await element.play()

    _scheduleMelody(context, destination)

    await new Promise(resolve => setTimeout(resolve, SPEAKER_TEST_DURATION_IN_MS))
  } finally {
    if (element.srcObject === toneStream) {
      element.srcObject = null
    }

    toneStream.getTracks().forEach(track => track.stop())
    context.close().catch(() => {})
  }
}
